import { debounce } from '@cc-shopping/utils/function/debounce';
import { Box, type BoxProps } from '@mui/material';
import { type PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { isSxArray } from 'src/shared/utils/isSxArray';
import { useCompareContext } from '../../../Context';
import { MAXIMUM_COLUMNS_MOBILE, useCreditCardCompareColumnContext } from '../../ColumnContext';
type ProductCardApplyNowContainerProps = {
  containerProps?: BoxProps;
  needsFixedHeight?: boolean;
};
export function ProductCardApplyNowContainer({
  children,
  containerProps,
  needsFixedHeight = false
}: PropsWithChildren<ProductCardApplyNowContainerProps>) {
  const {
    anyRatesAndFeesOffer,
    showStickyHeader,
    setApplyNowHeightsByIndex,
    tallestApplyNow,
    calculateMaxApplyNowHeight
  } = useCompareContext();
  const {
    index
  } = useCreditCardCompareColumnContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasSetValue, setHasSetValue] = useState(false);
  const {
    sx
  } = containerProps ?? {};
  const extendedSx = sx && isSxArray<BoxProps['sx']>(sx) ? sx : sx ? [sx] : [];
  useEffect(() => {
    if (anyRatesAndFeesOffer) {
      calculateMaxApplyNowHeight();
    } else {
      setHasSetValue(false);
    }
  }, [anyRatesAndFeesOffer]);
  const debouncedResetHeight = useCallback(debounce(() => {
    setHasSetValue(false);
  }, 100), []);
  useEffect(() => {
    if (needsFixedHeight) {
      const handleResize = () => debouncedResetHeight();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [needsFixedHeight]);
  useEffect(() => {
    const currentRef = containerRef.current;
    if (currentRef && !hasSetValue && needsFixedHeight) {
      const rect = currentRef.getBoundingClientRect();
      setApplyNowHeightsByIndex(prevValue => {
        if (!prevValue[index] || prevValue[index] !== rect.height) {
          prevValue[index] = rect.height;
          return [...prevValue];
        }
        return prevValue;
      });
      setHasSetValue(true);
      calculateMaxApplyNowHeight();
    }
  }, [index, hasSetValue, containerRef.current]);
  return <Box ref={containerRef} {...hasSetValue && needsFixedHeight && {
    height: `${tallestApplyNow}px`
  }} {...containerProps} sx={[{
    px: needsFixedHeight ? 3 : 0,
    pb: needsFixedHeight ? 1 : 0,
    display: {
      md: 'block',
      xs: index > MAXIMUM_COLUMNS_MOBILE - 1 ? 'none' : 'block'
    }
  }, theme => ({
    borderLeft: index !== 0 && !showStickyHeader ? `1px solid ${theme.palette.neutral.lighter}` : 0
  }), ...extendedSx]}>
      {children}
    </Box>;
}